import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';

export interface PlaylistItem {
  id: number;
  type: 'movie' | 'tv';
  title: string;
  posterPath: string | null;
  year?: string | null; // release year, e.g. "2019"
  addedAt: string;
}

export interface Playlist {
  id: string;
  ownerId: string;
  ownerName: string;
  title: string;
  description: string;
  items: PlaylistItem[];
  isPublic: boolean;
  createdAt?: any;
  updatedAt?: any;
}

const PLAYLISTS_COLLECTION = 'playlists';

export const PlaylistService = {
  async createPlaylist(ownerId: string, ownerName: string, title: string, description = ''): Promise<string> {
    const ref = await addDoc(collection(db, PLAYLISTS_COLLECTION), {
      ownerId,
      ownerName,
      title,
      description,
      items: [],
      isPublic: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  },

  async getUserPlaylists(ownerId: string): Promise<Playlist[]> {
    // Sorted client-side to avoid needing a composite Firestore index
    const q = query(collection(db, PLAYLISTS_COLLECTION), where('ownerId', '==', ownerId));
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() } as Playlist))
      .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
  },

  async getPlaylist(id: string): Promise<Playlist | null> {
    const snap = await getDoc(doc(db, PLAYLISTS_COLLECTION, id));
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as Playlist) : null;
  },

  async updatePlaylist(id: string, updates: Partial<Pick<Playlist, 'title' | 'description' | 'isPublic' | 'items'>>): Promise<void> {
    await updateDoc(doc(db, PLAYLISTS_COLLECTION, id), {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  },

  async deletePlaylist(id: string): Promise<void> {
    await deleteDoc(doc(db, PLAYLISTS_COLLECTION, id));
  },

  async addItem(playlist: Playlist, item: Omit<PlaylistItem, 'addedAt'>): Promise<void> {
    if (playlist.items.some(i => i.id === item.id && i.type === item.type)) return;
    await updateDoc(doc(db, PLAYLISTS_COLLECTION, playlist.id), {
      items: [...playlist.items, { ...item, addedAt: new Date().toISOString() }],
      updatedAt: serverTimestamp(),
    });
  },

  async removeItem(playlist: Playlist, item: PlaylistItem): Promise<void> {
    await updateDoc(doc(db, PLAYLISTS_COLLECTION, playlist.id), {
      items: arrayRemove(item),
      updatedAt: serverTimestamp(),
    });
  },
};

export function playlistShareUrl(id: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://epify.tv';
  return `${base}/list/${id}`;
}

export function playlistEmbedCode(id: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://epify.tv';
  return `<iframe src="${base}/list/${id}/embed" width="100%" height="420" frameborder="0" style="border-radius:12px;overflow:hidden" title="Epify playlist"></iframe>`;
}
